import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { RentEntry, PaymentMethod } from '@/api/types';
import { X } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { paymentMethodLabel } from '@/lib/labels';
import { ReceiptAttachField } from '@/features/rent/ReceiptAttachField';
import { uploadReceipt } from '@/features/rent/receiptApi';

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Same field layout as the owner/supervisor "Collect Payment" popup (amount, method,
// reference, receipt attachment) — the only difference is this submits for owner/supervisor
// review instead of recording the payment directly.
export function PaymentReceiptUploadModal({ entry, onClose }: { entry: RentEntry; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [amount, setAmount] = useState(String(Math.max(0, Number(entry.amountDue) - Number(entry.amountPaid))));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('UPI');
  const [transactionRef, setTransactionRef] = useState('');
  const [paidAt, setPaidAt] = useState(todayStr());
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const refRequired = paymentMethod === 'UPI' || paymentMethod === 'BANK_TRANSFER';

  const submitMutation = useMutation({
    mutationFn: async () => {
      const receiptUrl = await uploadReceipt(receiptFile!, entry.hostelId, entry.tenantId);
      return client.post(`/api/rent/${entry.id}/payment-submissions`, {
        receiptUrl,
        amount: Number(amount),
        paymentMethod,
        transactionRef: transactionRef || undefined,
        paidAt,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-portal-payment-submissions'] });
      onClose();
    },
  });

  const canSubmit = !!amount && !!paidAt && !!receiptFile && (!refRequired || !!transactionRef.trim());

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-chrome-page rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">{t('tenant.uploadReceiptTitle', 'Upload Payment Receipt')}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500">
            {t('tenant.uploadReceiptHelp2', 'Tell us how you paid and attach a copy of the receipt — the owner/supervisor will review and confirm it.')}
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.amount')} *</label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('rent.paymentMethod')}</label>
            <Select
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value as PaymentMethod)}
              options={(['CASH', 'UPI', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE', 'OTHER'] as PaymentMethod[]).map(m => ({ value: m, label: paymentMethodLabel(t, m) }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('owner.transactionRef', 'Transaction / Reference ID')}{refRequired && ' *'}
            </label>
            <input
              value={transactionRef}
              onChange={e => setTransactionRef(e.target.value)}
              placeholder={refRequired
                ? t('owner.upiRefPlaceholderRequired', 'Enter unique UPI / bank transaction ID…')
                : t('owner.upiRefPlaceholder', 'UPI ID / Ref number…')}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            {refRequired && (
              <p className="text-[11px] text-gray-400 mt-1">
                {t('owner.transactionRefHint', 'Must be unique — the same reference cannot be used for two payments.')}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('tenant.paidOn', 'Paid On')} *</label>
            <input
              type="date"
              value={paidAt}
              onChange={e => setPaidAt(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <ReceiptAttachField file={receiptFile} onChange={setReceiptFile} label={t('tenant.receiptRequired', 'Receipt (required)')} />

          {submitMutation.isError && (
            <p className="text-red-600 text-sm">{(submitMutation.error as Error).message}</p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => submitMutation.mutate()}
              disabled={!canSubmit || submitMutation.isPending}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
            >
              {submitMutation.isPending ? t('tenant.saving', 'Saving…') : t('common.submit')}
            </button>
            <button onClick={onClose} className="flex-1 border rounded-lg py-2 text-sm">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
